import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Button, Typography, Box } from '@mui/material'
import React, { useCallback } from 'react'
import { DATA_SECURITY_EMAIL } from 'src/Configuration/addresses'
import LinkVariant from 'src/Elements/Buttons/LinkVariant'
import useModals from 'src/Hooks/useModals'
import { useUserRoles } from 'src/Hooks/user'
/* this feature is excluded with CPE-2006 */
// import useModals from 'src/Hooks/useModals'
import { NdaDownloadButton } from 'src/Libs/nda'
import { Company, UserData } from 'src/State/User/UserModel'
import { makeStyles } from 'tss-react/mui'
import PartnerNumber from './PartnerNumber'
import { Stack } from '@mui/system'
import { USER_PROFILE_DETAILS_ID } from 'src/Libs/qa/ids'
import { isFlowTheme } from 'src/Libs/utils'

const useStyles = makeStyles()(theme => ({
  contentItem: {
    display: 'flex',
    flex: '1 1 45%',

    [theme.breakpoints.down('sm')]: {
      flexBasis: 'auto',
    },
  },
  infoText: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  infoAssignedCompany: {
    display: 'flex',
    flexDirection: 'column',
  },
  companyEmail: {
    color: theme.palette.primary.main,
  },
  flex: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
}))

interface Props {
  company?: Company
  user?: UserData
}

const ProfileDetails: React.FC<Props> = ({ company, user }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { isInstaller, isPartner } = useUserRoles()
  const { openDeleteProfileDialog, openPasswordEditor } = useModals()
  const { openCookieModal } = useModals()
  const showNdaButton = !!company?.NDA_submit && (isInstaller || isPartner)

  const handleResetPassword = useCallback(() => {
    if (!user) return
    openPasswordEditor({ user })
  }, [user, openPasswordEditor])

  /* this feature is excluded with CPE-2006 */
  // const { openCompanyEditor } = useModals()

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }} data-qa={USER_PROFILE_DETAILS_ID}>
      {/* (Data) Security */}
      <Paper title={t('user.userProfile.security')} className={classes.contentItem} contentClassName={classes.flex}>
        <Box className={classes.infoText} pt={4}>
          <Typography variant={'body2'}>{t('user.userProfile.securityQuestions')}</Typography>
          <LinkVariant
            type="email"
            link={DATA_SECURITY_EMAIL}
            label={t('user.userProfile.clickHere')}
            linkClassName={classes.companyEmail}
            hasUnderline={false}
            textVariant="body2"
            textClassName={classes.companyEmail}
          />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="inherit" mt={2} flexWrap={'wrap'}>
          <Button variant="outlined" size="large" color="primary" onClick={openDeleteProfileDialog}>
            {t('user.userProfile.delete-user-account')}
          </Button>
          <Button type="button" variant="outlined" size="large" color="primary" onClick={() => openCookieModal({ isOnProfilePage: true })}>
            {t('user.userProfile.coockies')}
          </Button>
          {!isFlowTheme && (
            <Button type="button" variant="outlined" color="primary" onClick={handleResetPassword}>
              {t('user.changePassword')}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Company Affilitation*/}
      {company?.id && (
        <Paper title={t('user.userProfile.companyAffiliation')} className={classes.contentItem}>
          <Box className={classes.infoAssignedCompany} pt={4}>
            <Typography variant={'body2'}>{company.Name}</Typography>
            <Typography variant={'body2'}>{company.contactData?.PLZ + ' ' + company.contactData?.Address}</Typography>
            <Typography variant={'body2'}>{company.contactData?.Telefon}</Typography>
            {company.contactData?.Email && (
              <LinkVariant
                type={'email'}
                textVariant="body2"
                link={company.contactData.Email}
                linkClassName={classes.companyEmail}
                hasUnderline={false}
              />
            )}
            {company.partnerNumber && <PartnerNumber partnerNumber={company.partnerNumber} />}
          </Box>
          {showNdaButton && (
            <Box sx={{ display: 'flex', flex: 1, mt: 2 }}>
              <NdaDownloadButton companyId={company?.id} companyName={company?.Name} />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  )
}

export default ProfileDetails
